import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Dayjs } from 'dayjs'
import debounce from 'lodash.debounce'
import {
  Autocomplete,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import useLiveListFilterBuilder from '@/hooks/useLiveListFilterBuilder'
import { OverlayState } from '@/hooks/useOverlay'
import usePickerFieldData from '@/hooks/usePickerFieldData'
import { useGetDataListColumns } from '@/service-library/hooks/data-list-columns'
import { useGetChildDataList } from '@/service-library/hooks/data-lists'
import { useGetIntegrationLiveListTypes } from '@/service-library/hooks/integration-live-list-types'
import { useGetLiveListFilterData } from '@/services/hooks/relay/dynamic'
import { DocumentRow } from '@/types/documents'
import { ProjectGridField } from '@/types/fields'
import { LiveListEntity } from '@/types/live-lists'
import { decodeHtml } from '@/utils/text'
import { isTimeFieldType } from '@/utils/typescript'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import {
  FormAutocomplete,
  FormDateTimeField,
  FormDecimalField,
  FormIntegerField,
  FormTextField,
  PixyDocsForm,
} from '@/components/forms'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { AutocompleteOption } from '@/components/validation/fields/AutocompleteField'
import { liveListNoneOption } from '../validation/fields/LiveListPickerField'
import useBulkChangeSet from './useBulkChangeSet'

type FormValues = {
  value: string
  dateTimeValue: Dayjs | null
  numberValue: number | ''
  pickerValue: AutocompleteOption | null
  checkboxValue: AutocompleteOption | null
}

export type BulkEditDialogProps = {
  overlay: OverlayState
  field: ProjectGridField
  selectedRows: Required<DocumentRow>[]
  workflowId: string
  workflowStateId: string
  listQueryKey?: QueryKey
  setPendingEdits: (
    fieldId: string,
    rowIdsByDoc: Map<string, Set<string>>,
  ) => void
}

export default function BulkEditValueDialog({
  overlay,
  field,
  selectedRows,
  workflowId,
  workflowStateId,
  listQueryKey,
  setPendingEdits,
}: BulkEditDialogProps) {
  const { project, fieldsMap } = useProjectContext()

  const { submit, isSaving } = useBulkChangeSet({
    field,
    workflowId,
    workflowStateId,
    listQueryKey,
    setPendingEdits,
    overlay,
    successMessage: (docCount) =>
      `Updating ${field.name} for ${docCount} document${
        docCount === 1 ? '' : 's'
      }.`,
    errorMessage:
      'Failed to update some document values. Please try again later.',
  })

  const fieldTypeCode = field.project_grid_field_type.code
  const isPicker = fieldTypeCode === 'picker'
  const isLiveListPicker = fieldTypeCode === 'live-list-picker'

  const parentField = fieldsMap[field.id]?.parentField
  const isTableField = !!parentField
  const tableFieldName = parentField?.name ?? ''

  const numberOfDocuments = useMemo(
    () => new Set(selectedRows.map((row) => row.document_id)).size,
    [selectedRows],
  )
  const isCheckbox = fieldTypeCode === 'checkbox'
  const isDateTimeType = isTimeFieldType(fieldTypeCode)
  const isInteger = fieldTypeCode === 'int'
  const isDecimal = fieldTypeCode === 'decimal'
  const isCurrency = fieldTypeCode === 'currency'

  const methods = useForm<FormValues>({
    defaultValues: {
      value: '',
      dateTimeValue: null,
      numberValue: '',
      pickerValue: null,
      checkboxValue: null,
    },
  })

  const {
    formState: { isDirty },
    reset,
    watch,
  } = methods

  const pickerFormValue = watch('pickerValue')

  const [liveListValue, setLiveListValue] =
    useState<LiveListEntity<unknown> | null>(null)
  const [liveListSearchValue, setLiveListSearchValue] = useState('')
  const debouncedSetLiveListSearch = useMemo(
    () => debounce(setLiveListSearchValue, 300),
    [],
  )
  const [filterString, setFilterString] = useState('')

  // Reset state when dialog opens
  useEffect(() => {
    if (overlay.isOpen) {
      reset({
        value: '',
        dateTimeValue: null,
        numberValue: '',
        pickerValue: null,
        checkboxValue: null,
      })
      setLiveListValue(null)
      setLiveListSearchValue('')
      setFilterString('')
    }
  }, [overlay.isOpen, reset])

  // Picker-specific data loading
  const {
    data_list_id: metadataListId,
    data_list_column_id: metadataColumnId,
  } = field.metadata

  const {
    data_list_id: paramsListId,
    data_list_column_names: paramsColumnNames,
  } = field.params || {}

  // For bulk edit, we use the first selected row's document org to resolve the child data list
  const firstDocOrgId = selectedRows[0]?.document?.owner_org_id

  const { dataList } = useGetChildDataList({
    orgId: firstDocOrgId || '',
    id: paramsListId || metadataListId || '',
    enabled:
      isPicker &&
      overlay.isOpen &&
      !!firstDocOrgId &&
      !!(paramsListId || metadataListId),
  })

  const { dataListColumns } = useGetDataListColumns({
    filters: {
      limit: '1000',
      data_list_id: paramsListId || metadataListId || '',
    },
    enabled: isPicker && overlay.isOpen,
  })

  const columnsMapping = useMemo(() => {
    if (!dataListColumns.length) return {}
    return dataListColumns.reduce<Record<string, string>>((acc, column) => {
      acc[column.id] = column.name
      return acc
    }, {})
  }, [dataListColumns])

  const dataListColumnNames = useMemo(() => {
    if (paramsColumnNames) return paramsColumnNames
    if (metadataColumnId && columnsMapping[metadataColumnId])
      return [columnsMapping[metadataColumnId]]
    return []
  }, [paramsColumnNames, metadataColumnId, columnsMapping])

  const { options: pickerOptions } = usePickerFieldData({
    columnsMapping,
    dataListId: dataList?.id || paramsListId || metadataListId || '',
    dataListColumnNames,
    filterString,
    useFilter: !!filterString,
    value: pickerFormValue,
    enabled: isPicker && overlay.isOpen,
  })

  // Live list picker data loading
  const { integrationLiveListTypes } = useGetIntegrationLiveListTypes({
    filters: {
      org_credential_type_id: project?.org_credential_type_id || '',
      data_format: 'live-list-entity',
      info_type_hints__id: field.info_type_id || '',
      fields__include: 'dependent_info_types_ids',
    },
    enabled: isLiveListPicker && overlay.isOpen,
  })

  const liveListExtra = useMemo(
    () => ({
      org_id: firstDocOrgId,
      org_credential_type_id: project?.org_credential_type_id || undefined,
      doc_id: selectedRows[0]?.document_id,
      doc_row_id: selectedRows[0]?.id,
      query: liveListSearchValue,
    }),
    [
      firstDocOrgId,
      project?.org_credential_type_id,
      selectedRows,
      liveListSearchValue,
    ],
  )

  const { liveListUrl, domainFilters } = useLiveListFilterBuilder({
    storedFilter: field.live_list_filter_config,
    infoTypeId: field.info_type_id,
    document: selectedRows[0]?.document,
    documentRow: selectedRows[0],
    extra: liveListExtra,
  })

  const { allData: liveListOptions, isPending: liveListIsPending } =
    useGetLiveListFilterData({
      liveListUrl,
      domainFilters,
      enabled:
        isLiveListPicker &&
        overlay.isOpen &&
        !!project.org_credential_type_id &&
        integrationLiveListTypes.length > 0 &&
        !!firstDocOrgId,
    })

  const allLiveListOptions = useMemo(() => {
    if (liveListIsPending) return []
    if (
      !liveListSearchValue ||
      'none'.includes(liveListSearchValue.trim().toLowerCase())
    ) {
      return [liveListNoneOption, ...liveListOptions]
    }
    return liveListOptions
  }, [liveListOptions, liveListIsPending, liveListSearchValue])

  function handleSubmit({
    value,
    dateTimeValue,
    numberValue,
    pickerValue,
    checkboxValue,
  }: FormValues) {
    const newValue = (() => {
      if (isLiveListPicker)
        return liveListValue?.entity_id === 'None'
          ? ''
          : liveListValue?.pd_display_name || ''
      if (isPicker)
        return pickerValue?.id === 'None' ? '' : pickerValue?.label || ''
      if (isCheckbox) return checkboxValue?.id || ''
      if (isDateTimeType) {
        if (!dateTimeValue) return ''
        if (fieldTypeCode === 'date') return dateTimeValue.format('YYYY-MM-DD')
        if (fieldTypeCode === 'time') return dateTimeValue.format('HH:mm:ss')
        return dateTimeValue.toISOString()
      }
      if (isInteger || isDecimal || isCurrency)
        return numberValue === '' ? '' : String(numberValue)
      return value
    })()

    const newRefId =
      isPicker && pickerValue && pickerValue.id !== 'None'
        ? pickerValue.id
        : null

    const newJsonValue =
      isLiveListPicker && liveListValue && liveListValue.entity_id !== 'None'
        ? liveListValue
        : isLiveListPicker
        ? null
        : undefined

    submit(selectedRows, {
      manual_value: newValue,
      manual_ref_id: newRefId,
      manual_value_json: newJsonValue,
    })
  }

  const hasValue = isLiveListPicker ? liveListValue !== null : isDirty

  return (
    <Dialog
      title={`Edit ${field.name}`}
      maxWidth="sm"
      {...overlay}
      onClose={() => overlay.close()}
    >
      <PixyDocsForm methods={methods} onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Set the value of <strong>{field.name}</strong> for{' '}
              {isTableField ? (
                <>
                  <strong>
                    {selectedRows.length} {tableFieldName} row
                    {selectedRows.length === 1 ? '' : 's'}
                  </strong>{' '}
                  across{' '}
                  <strong>
                    {numberOfDocuments} document
                    {numberOfDocuments === 1 ? '' : 's'}
                  </strong>
                </>
              ) : (
                <>
                  <strong>
                    {numberOfDocuments} selected document
                    {numberOfDocuments === 1 ? '' : 's'}
                  </strong>
                </>
              )}
              .
            </Typography>

            {isPicker ? (
              <FormAutocomplete<FormValues, AutocompleteOption>
                name="pickerValue"
                label={field.name}
                autoFocus
                openOnFocus
                options={pickerOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                textFieldProps={{
                  slotProps: { inputLabel: { shrink: true } },
                }}
                componentsProps={{
                  popper: { placement: 'bottom' },
                }}
              />
            ) : isLiveListPicker ? (
              <Autocomplete<LiveListEntity<unknown>, false, false, false>
                autoHighlight
                autoComplete
                openOnFocus
                clearOnEscape
                options={allLiveListOptions}
                value={liveListValue}
                onChange={(_e, newValue) => setLiveListValue(newValue)}
                onInputChange={(_e, newInputValue, reason) => {
                  if (reason === 'input') {
                    debouncedSetLiveListSearch(newInputValue)
                  }
                }}
                getOptionLabel={(option) =>
                  decodeHtml(option.pd_display_name) || ''
                }
                isOptionEqualToValue={(option, value) =>
                  option?.entity_id === value.entity_id
                }
                groupBy={
                  allLiveListOptions.length > 1 &&
                  allLiveListOptions[1]?.pd_group_value
                    ? (option) => option.pd_group_value || 'None'
                    : undefined
                }
                loading={liveListIsPending}
                filterOptions={(x) => x}
                renderOption={(props, option) => (
                  <MenuItem
                    {...props}
                    key={option.entity_id}
                    value={option.entity_id}
                    sx={{ alignItems: 'center' }}
                  >
                    {decodeHtml(option.pd_display_name)}
                    {option.pd_display_detail && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1, mt: '1px' }}
                      >
                        {option.pd_display_detail}
                      </Typography>
                    )}
                  </MenuItem>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoFocus
                    label={field.name}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
                sx={{ width: '100%' }}
              />
            ) : isCheckbox ? (
              <FormAutocomplete<FormValues, AutocompleteOption>
                name="checkboxValue"
                label={field.name}
                autoFocus
                openOnFocus
                options={[
                  { id: 'True', label: 'Checked' },
                  { id: 'False', label: 'Unchecked' },
                ]}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                textFieldProps={{
                  slotProps: { inputLabel: { shrink: true } },
                }}
                componentsProps={{
                  popper: { placement: 'bottom' },
                }}
              />
            ) : isDateTimeType ? (
              <FormDateTimeField
                name="dateTimeValue"
                label={field.name}
                type={fieldTypeCode as 'date' | 'time' | 'datetime'}
                autoFocus
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            ) : isCurrency || isDecimal ? (
              <FormDecimalField
                name="numberValue"
                label={field.name}
                autoFocus
                slotProps={{ inputLabel: { shrink: true } }}
              />
            ) : isInteger ? (
              <FormIntegerField
                name="numberValue"
                label={field.name}
                autoFocus
                slotProps={{ inputLabel: { shrink: true } }}
              />
            ) : (
              <FormTextField
                name="value"
                autoFocus
                fullWidth
                label={field.name}
                shrinkLabel
              />
            )}
          </Stack>
        </DialogContent>
        <DialogFooter>
          <Button
            variant="text"
            onClick={() => overlay.close()}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!hasValue || isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
          >
            Save
          </Button>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
